import { useEffect, useState } from 'react';

/**
 * Tracks connectivity from the browser's online/offline events.
 *
 * `navigator.onLine` only proves a network interface exists — a captive portal or
 * a dead uplink still reads as online. That is acceptable here because the UI uses
 * it for one thing: showing the OfflineBanner and disabling submit buttons that
 * are guaranteed to fail. Anything that must actually know reachability learns it
 * from a failed request (ApiError.kind === 'network'), not from this hook.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Re-sync on mount: events fired before this effect ran would be missed.
    setOnline(navigator.onLine !== false);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
